import { useEffect, useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  ChevronDown,
  ExternalLink,
  FileText,
  Gem,
  HeartHandshake,
  Home,
  Image,
  Images,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  MessageSquareQuote,
  Newspaper,
  ScrollText,
  Settings,
  Users,
  Wallet,
  X,
  Youtube,
} from "lucide-react";
import { BkaWordmark } from "@/components/BkaLogo";
import { ROLE_LABELS, useAuth } from "@/context/AuthContext";
import { AdminSpinner } from "@/components/admin/AdminUi";

const NAV = [
  { label: "Dashboard", href: "/admin", Icon: LayoutDashboard, end: true },
  { label: "Homepage", href: "/admin/homepage", Icon: Home },
  { label: "Pages", href: "/admin/pages", Icon: FileText },
  { label: "Programs / Our Efforts", href: "/admin/programs", Icon: HeartHandshake },
  { label: "Blog / News", href: "/admin/posts", Icon: Newspaper },
  { label: "Videos", href: "/admin/videos", Icon: Youtube },
  { label: "Testimonials", href: "/admin/testimonials", Icon: MessageSquareQuote, cap: "moderate" },
  { label: "Forms & Enquiries", href: "/admin/enquiries", Icon: Mail, cap: "moderate" },
  {
    label: "Emerald Honours",
    Icon: Gem,
    children: [
      { label: "Nominations", href: "/admin/emerald/nominations", cap: "moderate" },
      { label: "Emerald Gallery (Photos)", href: "/admin/emerald/gallery" },
      { label: "Emerald Media / Videos", href: "/admin/emerald/media" },
      { label: "Emerald Settings", href: "/admin/emerald/settings", cap: "settings.write" },
    ],
  },
  { label: "Galleries", href: "/admin/galleries", Icon: Images },
  { label: "Media Library", href: "/admin/media", Icon: Image },
  { label: "Donation Settings", href: "/admin/donations", Icon: Wallet, cap: "settings.write" },
  { label: "Users & Roles", href: "/admin/users", Icon: Users, cap: "users.manage" },
  { label: "Settings", href: "/admin/settings", Icon: Settings, cap: "settings.write" },
  { label: "Audit Log", href: "/admin/audit-log", Icon: ScrollText, cap: "audit.read" },
];

export default function AdminLayout() {
  const { user, signOut, can } = useAuth();
  const location = useLocation();
  const [drawer, setDrawer] = useState(false);
  const [emeraldOpen, setEmeraldOpen] = useState(true);

  useEffect(() => setDrawer(false), [location.pathname]);

  if (user === null) return <AdminSpinner label="Checking your session" />;
  if (user === false) return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;

  const allowed = (item) => !item.cap || can(item.cap);

  const sidebar = (
    <div className="flex h-full flex-col bg-bka-navy text-white">
      <div className="flex items-center gap-3 border-b border-white/10 px-5 py-5">
        <BkaWordmark tone="dark" compact />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="CMS navigation">
        {NAV.filter(allowed).map((item) => {
          if (item.children) {
            const kids = item.children.filter(allowed);
            if (!kids.length) return null;
            return (
              <div key={item.label} className="mt-1">
                <button
                  type="button"
                  data-testid="cms-sidebar-emerald-group"
                  onClick={() => setEmeraldOpen((v) => !v)}
                  aria-expanded={emeraldOpen}
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <item.Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1 text-left">{item.label}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${emeraldOpen ? "rotate-180" : ""}`} />
                </button>
                {emeraldOpen && (
                  <div className="ml-4 mt-1 space-y-0.5 border-l border-white/10 pl-3">
                    {kids.map((k) => (
                      <NavLink
                        key={k.href}
                        to={k.href}
                        data-testid={`cms-sidebar-${k.href.split("/").pop()}-link`}
                        className={({ isActive }) =>
                          `block rounded-lg px-3 py-2 text-[13px] font-semibold transition-colors ${
                            isActive ? "bg-bka-emerald text-white" : "text-white/65 hover:bg-white/10 hover:text-white"
                          }`
                        }
                      >
                        {k.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          return (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.end}
              data-testid={`cms-sidebar-${item.href.split("/").pop() || "dashboard"}-link`}
              className={({ isActive }) =>
                `mt-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
                  isActive ? "bg-bka-emerald text-white" : "text-white/80 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <item.Icon className="h-4 w-4 shrink-0" />
              {item.label}
            </NavLink>
          );
        })}

        <a
          href="/"
          target="_blank"
          rel="noreferrer"
          data-testid="cms-sidebar-view-website"
          className="mt-5 flex items-center gap-3 rounded-xl border-t border-white/10 px-3 pt-5 text-sm font-semibold text-white/70 transition-colors hover:text-white"
        >
          <ExternalLink className="h-4 w-4 shrink-0" />
          View Website
        </a>
      </nav>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[264px] lg:block">{sidebar}</aside>

      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setDrawer(false)}
            className="absolute inset-0 bg-bka-navyDeep/50"
          />
          <div className="relative h-full w-[264px]" data-testid="cms-mobile-sidebar">
            {sidebar}
          </div>
        </div>
      )}

      <div className="lg:pl-[264px]">
        <header className="sticky top-0 z-30 flex h-[68px] items-center justify-between gap-4 border-b border-slate-200 bg-white px-5 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="cms-mobile-menu-toggle"
              onClick={() => setDrawer((v) => !v)}
              aria-label="Toggle CMS menu"
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-bka-navy lg:hidden"
            >
              {drawer ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
            <div>
              <p className="font-display text-base font-bold text-bka-navy">Welcome back, {user.name?.split(" ")[0]}</p>
              <p className="hidden text-xs text-slate-500 sm:block">
                Manage content, engagement and make a bigger impact.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <p className="text-sm font-bold text-bka-navy">{user.name}</p>
              <p className="text-xs text-slate-500">{ROLE_LABELS[user.role] || user.role}</p>
            </div>
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-bka-emerald text-sm font-bold text-white">
              {(user.name || "?")
                .split(" ")
                .map((n) => n[0])
                .slice(0, 2)
                .join("")}
            </span>
            <button
              type="button"
              data-testid="cms-sign-out"
              onClick={signOut}
              aria-label="Sign out"
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition-colors hover:border-red-300 hover:text-red-600"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>

        <main className="p-5 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function RequireCapability({ capability, children }) {
  const { can, user } = useAuth();
  if (user === null) return <AdminSpinner />;
  if (!can(capability))
    return (
      <div data-testid="cms-forbidden" className="rounded-2xl border border-amber-200 bg-amber-50 p-8">
        <h1 className="font-display text-xl font-bold text-amber-900">You don't have access to this area</h1>
        <p className="mt-2 text-sm text-amber-800">
          Your role ({ROLE_LABELS[user.role] || user.role}) does not include permission for this section. Ask a
          Super Admin if you need access.
        </p>
        <Link to="/admin" className="mt-5 inline-block text-sm font-bold text-bka-emerald">
          ← Back to Dashboard
        </Link>
      </div>
    );
  return children;
}
